import {
  SerializedInventoryLocation,
  LoraDevice,
  SerializedInventoryMeter,
  SerializedMeterModel,
} from '@enmon/commons/lib/tools';
import { InventoryLocationBuildingsFilter } from 'services/api/inventoryLocationBuildings';
import { FindInventoryMeterReadingGroupArgs } from 'services/api/inventoryMeterReadingGroups';
import { MeterModelsFilter } from 'services/api/meterModels';
import { ThirdPartiesFilter } from 'services/api/thirdParties';
import { SerializedFilter, SerializedMeter, ViewMeters } from '@enmon/commons/lib/enmon';
import { MeterRegister, StrictOmit } from '@enmon/commons';

import { DecoderFilter, FilesFilter } from '../services/api/enmonTools';
import { GetLocationMetersFilter, InventoryLocationsFilter } from '../services/api/inventoryLocations';

export const QUERY_KEYS = {
  auth: (apiKey: SerializedInventoryLocation['api_key']) => ['auth', apiKey] as const,
  enmonTools: {
    decoder: {
      all: () => ['enmonTools', 'decoder'] as const,
      filter: (filter: DecoderFilter) => [...QUERY_KEYS.enmonTools.decoder.all(), filter] as const,
      getDecodedMessages: (filter: DecoderFilter) =>
        [...QUERY_KEYS.enmonTools.decoder.filter(filter), 'getDecodedMessages'] as const,
    },
    meters: {
      all: () => ['enmonTools', 'meters'] as const,
      filter: (filter: SerializedFilter & { view?: ViewMeters } = {}) =>
        [...QUERY_KEYS.enmonTools.meters.all(), filter] as const,
      one: (id: SerializedMeter['_id'], filter: SerializedFilter = {}) =>
        [...QUERY_KEYS.enmonTools.meters.filter(filter), id] as const,
    },
    files: {
      all: () => ['enmonTools', 'files'] as const,
      link: (fileId?: string) => ['enmonTools', 'files', fileId, 'link'] as const,
      filter: (filter: FilesFilter = {}) => [...QUERY_KEYS.enmonTools.files.all(), filter] as const,
    },
  },
  inventoryMeters: {
    all: () => ['inventory-meters'] as const,
    filter: (filter: SerializedFilter) => [...QUERY_KEYS.inventoryMeters.all(), filter] as const,
    one: (id: SerializedInventoryMeter['id']) => [...QUERY_KEYS.inventoryMeters.all(), 'one', id] as const,
  },
  inventoryLocations: {
    all: () => ['inventory-locations'] as const,
    filter: (filter: InventoryLocationsFilter = {}) => [...QUERY_KEYS.inventoryLocations.all(), filter] as const,
    getLocationMeters: (filter: GetLocationMetersFilter = {}) =>
      [...QUERY_KEYS.inventoryLocations.all(), 'getLocationMeters', filter] as const,
  },
  inventoryLocationBuildings: {
    all: () => ['inventory-location-buildings'] as const,
    filter: (filter: InventoryLocationBuildingsFilter = {}) =>
      [...QUERY_KEYS.inventoryLocationBuildings.all(), filter] as const,
  },
  inventoryMeterHardwareItems: {
    all: () => ['inventory-meter-hardware-items'] as const,
  },
  inventoryMeterReadingGroups: {
    all: () => ['inventory-meter-reading-groups'] as const,
    filter: (filter: FindInventoryMeterReadingGroupArgs['filter']) =>
      [...QUERY_KEYS.inventoryMeterReadingGroups.all(), filter] as const,
  },
  loraDevices: {
    all: 'loraDevices' as const,
    one: (loraDeviceId: LoraDevice['id']) => [QUERY_KEYS.loraDevices.all, loraDeviceId] as const,
    find: (loraDeviceAttributes: StrictOmit<Partial<LoraDevice>, 'id'>) =>
      [QUERY_KEYS.loraDevices.all, 'find', ...(loraDeviceAttributes ? [loraDeviceAttributes] : [])] as const,
  },
  lora: {
    messages: { all: (devEui: MeterRegister['dataSourceId']) => ['lora', 'messages', devEui] as const },
  },
  meterModels: {
    all: () => ['meterModels'] as const,
    one: (id?: SerializedMeterModel['id'] | null) => ['meterModels', id] as const,
    filter: (filter: MeterModelsFilter) => [...QUERY_KEYS.meterModels.all(), filter] as const,
  },
  inventoryMeterThirdParty: {
    one: (meterId: SerializedInventoryMeter['id']) => ['inventory-meter-third-party', meterId] as const,
  },
  thirdParties: {
    all: () => ['third-parties'] as const,
    filter: (filter: ThirdPartiesFilter) => [...QUERY_KEYS.thirdParties.all(), filter] as const,
  },
};

export const MUTATION_KEYS = {
  inventoryMeterReadingGroups: {
    all: () => ['inventory-meter-reading-groups'] as const,
    create: () => [...MUTATION_KEYS.inventoryMeterReadingGroups.all(), 'create'] as const,
    update: () => [...MUTATION_KEYS.inventoryMeterReadingGroups.all(), 'update'] as const,
    delete: () => [...MUTATION_KEYS.inventoryMeterReadingGroups.all(), 'delete'] as const,
  },
};
